"use client";

/**
 * BypassClerkProvider — drop-in replacement for ClerkProvider used when
 * the dev server is running with BYPASS_AUTH=1. It supplies a mock user
 * via React context so that useUser() / useClerk() hooks return realistic
 * data and the dashboard pages render as if the user is signed in.
 *
 * This is ONLY for visual verification of the dashboard in dev. It is
 * never rendered in production (the layout gates it on process.env.BYPASS_AUTH).
 *
 * The mock user id ("user_dev_bypass") is reserved; the rest of the system
 * treats it as a real clerk_id and looks it up in Supabase.
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";

interface BypassUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string | null;
  emailAddresses: Array<{ id: string; emailAddress: string }>;
  primaryEmailAddressId: string;
  imageUrl: string;
  publicMetadata: Record<string, unknown>;
  unsafeMetadata: Record<string, unknown>;
}

const MOCK_USER: BypassUser = {
  id: "user_dev_bypass",
  firstName: "Dev",
  lastName: "User",
  fullName: "Dev User",
  emailAddresses: [{ id: "email_dev_1", emailAddress: "dev@agenx.in" }],
  primaryEmailAddressId: "email_dev_1",
  imageUrl: "",
  publicMetadata: {},
  unsafeMetadata: {},
};

interface BypassContextValue {
  user: BypassUser;
  isLoaded: true;
  isSignedIn: true;
  signOut: () => Promise<void>;
}

// Exported so the client-shim can read from it.
export const BypassContext = createContext<BypassContextValue | null>(null);

export function BypassClerkProvider({ children }: { children: ReactNode }) {
  // Mark the window so the shim knows bypass is active (in case the
  // shim is used outside the provider subtree).
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as unknown as { __AGENX_BYPASS_AUTH__?: boolean }).__AGENX_BYPASS_AUTH__ = true;
    }
  }, []);

  const value: BypassContextValue = {
    user: MOCK_USER,
    isLoaded: true,
    isSignedIn: true,
    signOut: async () => {
      // No-op in bypass mode
    },
  };
  return <BypassContext.Provider value={value}>{children}</BypassContext.Provider>;
}

/**
 * useBypassUser — internal hook used by the useUser shim below.
 * Pages that import from "@clerk/nextjs" will get our mock via the
 * shim instead, so this stays internal.
 */
export function useBypassUser(): BypassContextValue {
  const ctx = useContext(BypassContext);
  if (!ctx) {
    throw new Error("useBypassUser must be used within BypassClerkProvider");
  }
  return ctx;
}
